import {createClient} from 'contentful';
import {documentToHtmlString} from '@contentful/rich-text-html-renderer';
import {Logger, Category} from '@simpleinvest/front-end-logger';
// eslint-disable-next-line import/no-cycle
import {GoalRouteNames} from '@client/routes/goal';
import {fallbackContent} from '@client/content/cms-fallback';

const logger = new Logger({
  category: Category.Cms,
  fileName: 'cms.js',
});

// TODO: remove after CMS is sorted out
// eslint-disable-next-line @typescript-eslint/naming-convention
let _contentful;

const stateObj = () => {
  _contentful = createClient({
    space: process.env.VUE_APP_CMS_SPACE,
    environment: process.env.VUE_APP_CMS_ENVIRONMENT,
    accessToken: process.env.VUE_APP_CMS_ACCESS_TOKEN,
  });

  // eslint-disable-next-line @typescript-eslint/naming-convention
  return {_static: {}};
};

const getters = {};

const actions = {
  async findPage({dispatch}, page) {
    let pageToPeek = page;

    const pagesThatUseAddGoal = [GoalRouteNames.GoalAllocationPage];

    if (pagesThatUseAddGoal.includes(page)) {
      pageToPeek = 'AddGoal';
    }

    const data = await dispatch('peekPage', pageToPeek);

    // null is for when page data was attempted but failed (for either http or nothing found
    // we don't differentiate)
    if (data === null) return null;

    return data || dispatch('fetchPage', pageToPeek);
  },
  // eslint-disable-next-line require-await
  async peekPage({state}, page) {
    return state[page];
  },
  async fetchPage({commit, dispatch}, page) {
    try {
      const data = await _contentful.getEntries({content_type: `pages_${page}`});

      if (!data.items || !data.items[0] || !data.items[0].fields) throw new Error(JSON.stringify(data));

      const {includes, items} = data;
      // eslint-disable-next-line unicorn/no-array-reduce, unicorn/prefer-object-from-entries
      const pageData = Object.keys(items[0].fields).reduce((acc, field) => {
        const value = items[0].fields[field];

        if (value.nodeType === 'document') {
          // eslint-disable-next-line no-param-reassign
          acc[field] = documentToHtmlString(value);

          return acc;
        }

        if (value.sys && value.sys.type === 'Asset') {
          const asset = includes.Asset.find((include) => include.sys.id === value.sys.id);

          if (!asset) {
            // eslint-disable-next-line no-param-reassign
            acc[field] = null;

            return acc;
          }

          // eslint-disable-next-line no-param-reassign
          acc[field] = asset.fields.file.url;

          return acc;
        }

        // eslint-disable-next-line no-param-reassign
        acc[field] = items[0].fields[field];

        return acc;
      }, {});

      await commit('setContent', {page, data: pageData});

      // eslint-disable-next-line unicorn/consistent-destructuring
      return data.items[0].fields;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(error);

      logger.addBreadcrumb({
        callingFuncName: 'fetchPage',
        message: `Contentful fetch error caught`,
      });
      logger.logException({error: errorObj});

      return dispatch('getFallbackContent', page);
    }
  },
  getFallbackContent({commit}, page) {
    try {
      const pageContent = fallbackContent[page];

      commit('setStaticContent', {page, data: pageContent});

      return pageContent;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(error);

      logger.addBreadcrumb({
        callingFuncName: 'getFallbackContent',
        message: `Failed to load fallback content`,
      });
      logger.logException({error: errorObj});

      return '';
    }
  },
};

const mutations = {
  setContent(state, {page, data}) {
    state[page] = data;
  },
  setStaticContent(state, {page, data}) {
    state._static[page] = data;
  },
};

// eslint-disable-next-line import/prefer-default-export
export const CMS = {
  namespaced: true,
  state: stateObj,
  getters,
  actions,
  mutations,
};
